import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CardActionArea,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Business as CompanyIcon,
  People as UserIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';
import { Task, Company, User } from '../../types';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalTasks: number;
  pendingTasks: number;
  completedTasks: number;
  totalCompanies: number;
  totalUsers: number;
}

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalTasks: 0,
    pendingTasks: 0,
    completedTasks: 0,
    totalCompanies: 0,
    totalUsers: 0
  });

  // --- NUEVO: Agrupar tareas de la semana ---
  const [weekTasks, setWeekTasks] = useState<Task[]>([]);
  // Estados para gráficos (solo Director)
  const [chartData, setChartData] = useState<any[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F'];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setError(null);
        const [tasksRes, companiesRes, usersRes] = await Promise.all([
          axiosInstance.get('/tasks'),
          user?.role === 'DIRECTOR' ? axiosInstance.get('/companies') : null,
          user?.role === 'DIRECTOR' ? axiosInstance.get('/users') : null
        ]);

        let tasks = tasksRes.data;
        if (!Array.isArray(tasks)) tasks = [];
        const companies = companiesRes?.data as Company[] || [];
        const users = usersRes?.data as User[] || [];

        setStats({
          totalTasks: tasks.length,
          pendingTasks: tasks.filter((t: Task) => t.status === 'PENDING').length,
          completedTasks: tasks.filter((t: Task) => t.status === 'COMPLETED').length,
          totalCompanies: companies.length,
          totalUsers: users.length
        });
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        setError(error.response?.data?.message || 'Error al cargar los datos del dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.role]);

  useEffect(() => {
    const fetchWeekTasks = async () => {
      try {
        const res = await axiosInstance.get('/tasks');
        let tasks: Task[] = Array.isArray(res.data) ? res.data : [];
        // Filtrar solo tareas no completadas y de esta semana
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        tasks = tasks.filter(t => {
          const date = new Date(t.dateTime);
          return (
            t.status !== 'COMPLETED' &&
            date >= startOfWeek &&
            date <= endOfWeek &&
            (user?.role !== 'MANAGER' || t.responsibleId === user.id)
          );
        });
        setWeekTasks(tasks);
      } catch (e) {
        setWeekTasks([]);
      }
    };
    fetchWeekTasks();
  }, [user?.role, user?.id]);

  // Cargar estadísticas de sucursales si es Director
  useEffect(() => {
    if (user?.role === 'DIRECTOR') {
      const fetchBranchStats = async () => {
        try {
          const res = await axiosInstance.get('/dashboard/branch-stats');
          if (res.data) {
            setChartData(res.data.data);
            setBranches(res.data.branches);
          }
        } catch (error) {
          console.error('Error fetching branch stats:', error);
        }
      };
      fetchBranchStats();
    }
  }, [user?.role]);

  // Agrupar tareas en Hoy, Mañana, Resto de la semana
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const endOfTomorrow = new Date(tomorrow);
  endOfTomorrow.setHours(23, 59, 59, 999);

  const tasksToday = weekTasks.filter(t => {
    const d = new Date(t.dateTime);
    return d >= today && d < tomorrow;
  });
  const tasksTomorrow = weekTasks.filter(t => {
    const d = new Date(t.dateTime);
    return d >= tomorrow && d < endOfTomorrow;
  });
  const tasksRest = weekTasks.filter(t => {
    const d = new Date(t.dateTime);
    return d >= endOfTomorrow;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3 }, background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)', minHeight: '100vh' }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '2.2rem', sm: '2.5rem' },
          mb: { xs: 2, sm: 3 },
          fontWeight: 700,
          color: '#1976d2',
          letterSpacing: 1
        }}
      >
        Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
          {error}
        </Alert>
      )}

      {/* NUEVO: Tareas en curso para esta semana */}
      <Box mb={4}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>Tareas en curso para esta semana</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #f5fafd 0%, #e3f0ff 100%)' }}>
              <CardContent>
                <Chip label="Hoy" color="primary" sx={{ mb: 2, fontWeight: 700, fontSize: '1rem', px: 2, py: 0.5 }} />
                {tasksToday.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay tareas para hoy.</Typography>
                ) : (
                  tasksToday.map(t => (
                    <Box key={t.id} mb={1}>
                      <CardActionArea component={Link} to={`/tasks/${t.id}`} sx={{ borderRadius: 2, transition: 'box-shadow 0.2s', boxShadow: 1, '&:hover': { boxShadow: 8, background: '#e3f0ff' } }}>
                        <Box p={1.5} borderRadius={2}>
                          <Typography variant="body1" fontWeight={600} color="#1976d2">{t.location}</Typography>
                          <Typography variant="caption" color="text.secondary">{new Date(t.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {t.company?.name}</Typography>
                        </Box>
                      </CardActionArea>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #fffbe7 0%, #ffe7e7 100%)' }}>
              <CardContent>
                <Chip label="Mañana" color="warning" sx={{ mb: 2, fontWeight: 700, fontSize: '1rem', px: 2, py: 0.5 }} />
                {tasksTomorrow.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay tareas para mañana.</Typography>
                ) : (
                  tasksTomorrow.map(t => (
                    <Box key={t.id} mb={1}>
                      <CardActionArea component={Link} to={`/tasks/${t.id}`} sx={{ borderRadius: 2, transition: 'box-shadow 0.2s', boxShadow: 1, '&:hover': { boxShadow: 8, background: '#fffbe7' } }}>
                        <Box p={1.5} borderRadius={2}>
                          <Typography variant="body1" fontWeight={600} color="#ff9800">{t.location}</Typography>
                          <Typography variant="caption" color="text.secondary">{new Date(t.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {t.company?.name}</Typography>
                        </Box>
                      </CardActionArea>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #e7ffe7 0%, #e3f0ff 100%)' }}>
              <CardContent>
                <Chip label="Resto de la semana" color="success" sx={{ mb: 2, fontWeight: 700, fontSize: '1rem', px: 2, py: 0.5 }} />
                {tasksRest.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No hay tareas para el resto de la semana.</Typography>
                ) : (
                  tasksRest.map(t => (
                    <Box key={t.id} mb={1}>
                      <CardActionArea component={Link} to={`/tasks/${t.id}`} sx={{ borderRadius: 2, transition: 'box-shadow 0.2s', boxShadow: 1, '&:hover': { boxShadow: 8, background: '#e7ffe7' } }}>
                        <Box p={1.5} borderRadius={2}>
                          <Typography variant="body1" fontWeight={600} color="#43a047">{t.location}</Typography>
                          <Typography variant="caption" color="text.secondary">{new Date(t.dateTime).toLocaleDateString()} {new Date(t.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {t.company?.name}</Typography>
                        </Box>
                      </CardActionArea>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Gráfico de Sucursales (Solo Director) */}
      {user?.role === 'DIRECTOR' && chartData.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>Comparativa Mensual por Sucursal</Typography>
          <Card sx={{ borderRadius: 4, boxShadow: 6, p: 2 }}>
            <CardContent sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {branches.map((branch, index) => (
                    <Bar key={branch} dataKey={branch} fill={COLORS[index % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)' }}>
            <CardActionArea component={Link} to="/tasks" sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1976d2',
                    fontWeight: 600
                  }}
                >
                  <TaskIcon sx={{ mr: 1, fontSize: { xs: '2rem', sm: '2.5rem' }, color: '#1976d2' }} /> 
                  Tareas
                </Typography>
                <Typography 
                  variant="h3"
                  sx={{ 
                    fontSize: { xs: '2.5rem', sm: '3rem' },
                    mb: 1,
                    color: '#1976d2',
                    fontWeight: 700
                  }}
                >
                  {stats.totalTasks}
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: '#1976d2',
                    fontWeight: 500
                  }}
                >
                  Pendientes: {stats.pendingTasks}
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: '#43a047',
                    fontWeight: 500
                  }}
                >
                  Completadas: {stats.completedTasks}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        {user?.role === 'DIRECTOR' && (
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #fffbe7 0%, #ffe7e7 100%)' }}>
              <CardActionArea component={Link} to="/companies" sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      display: 'flex',
                      alignItems: 'center',
                      color: '#ff9800',
                      fontWeight: 600
                    }}
                  >
                    <CompanyIcon sx={{ mr: 1, fontSize: { xs: '2rem', sm: '2.5rem' }, color: '#ff9800' }} /> 
                    Empresas
                  </Typography>
                  <Typography 
                    variant="h3"
                    sx={{ 
                      fontSize: { xs: '2.5rem', sm: '3rem' },
                      mb: 1,
                      color: '#ff9800',
                      fontWeight: 700
                    }}
                  >
                    {stats.totalCompanies}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
        {user?.role === 'DIRECTOR' && (
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: 6, background: 'linear-gradient(135deg, #e7ffe7 0%, #e3f0ff 100%)' }}>
              <CardActionArea component={Link} to="/users" sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      display: 'flex',
                      alignItems: 'center',
                      color: '#43a047',
                      fontWeight: 600
                    }}
                  >
                    <UserIcon sx={{ mr: 1, fontSize: { xs: '2rem', sm: '2.5rem' }, color: '#43a047' }} /> 
                    Usuarios
                  </Typography>
                  <Typography 
                    variant="h3"
                    sx={{ 
                      fontSize: { xs: '2.5rem', sm: '3rem' },
                      mb: 1,
                      color: '#43a047',
                      fontWeight: 700
                    }}
                  >
                    {stats.totalUsers}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard; 